// Pure helpers for "Read codeplug from radio" (Admin > Radios > Edit > DMR settings, IRC380): the
// radio reads the codeplug its own DMR worker has loaded and a platform administrator may save it
// as a NEW profile of the radio's network. Nothing here programs a radio or loads a profile.
import type { CodeplugCapture, CodeplugCaptureWarning } from '../../services/dmrCodeplug';
import { LIMITS } from './dmrCodeplugModel';

/** Poll the pending reading about every second; the server expires it after 45 s, so give up at 50 s. */
export const CAPTURE_POLL_MS = 1_000;
export const CAPTURE_TIMEOUT_MS = 50_000;

export type CapturePhase = 'idle' | 'starting' | 'pending' | 'ready' | 'saving' | 'saved' | 'failed';

/**
 * Whether the caller may read a codeplug out: a platform administrator with a VERIFIED sign-in
 * (identity "jwt" or "session"). The DEV_MODE identities ("dev-platform", "dev-user") never may,
 * also when they resolve to platform admin; a missing or unknown identity fails closed. UX only -
 * the server refuses the same callers (decisions.md "Codeplug capture requires a verified platform
 * administrator").
 */
export const canCaptureCodeplug = (me: { identity?: string; platform_admin?: boolean } | null | undefined): boolean =>
  !!me && me.platform_admin === true && (me.identity === 'jwt' || me.identity === 'session');

/**
 * The only origin where Microsoft sign-in works on the development stack: its registered redirect
 * is http://localhost:3000/api/auth/entra/callback (Setup-Entra.ps1 -Environment development;
 * Microsoft allows plain-http redirects to localhost only, entra.md).
 */
export const DEV_SIGN_IN_ORIGIN = 'http://localhost:3000';

export type CaptureAccess =
  | 'allowed'
  /** DEV_MODE identity on the host PC with Microsoft sign-in enabled: offer the sign-in. */
  | 'dev-sign-in'
  /** DEV_MODE identity from another origin (e.g. the LAN IP): sign-in only works on the host PC. */
  | 'dev-sign-in-on-host'
  /** DEV_MODE identity and no Microsoft sign-in configured. */
  | 'dev-no-sign-in'
  /** A verified sign-in without the platform administrator role: no sign-in offer. */
  | 'not-admin'
  | 'unknown';

/**
 * Why the button is (not) offered, and so which hint replaces it. The sign-in offer is UX only: the
 * server still admits only a verified platform administrator, and nothing forces a sign-in.
 */
export const captureAccess = (
  me: { identity?: string; platform_admin?: boolean } | null | undefined,
  entraEnabled: boolean,
  origin: string
): CaptureAccess => {
  if (canCaptureCodeplug(me)) return 'allowed';
  if (me?.identity === 'jwt' || me?.identity === 'session') return 'not-admin';
  if (me?.identity === 'dev-platform' || me?.identity === 'dev-user') {
    if (!entraEnabled) return 'dev-no-sign-in';
    return origin === DEV_SIGN_IN_ORIGIN ? 'dev-sign-in' : 'dev-sign-in-on-host';
  }
  return 'unknown';
};

const DEV_IDENTITY_TEXT =
  'Reading the codeplug from the radio needs a signed-in platform administrator; the local development identity is not enough.';

/** The hint shown instead of the button (empty when the button is offered). */
export const captureAccessText = (access: CaptureAccess): string => {
  switch (access) {
    case 'allowed':
      return '';
    case 'dev-sign-in':
      return `${DEV_IDENTITY_TEXT} Sign in with a Microsoft account linked to a RadioOS platform administrator (multi-factor authentication required); you return to this page.`;
    case 'dev-sign-in-on-host':
      return `${DEV_IDENTITY_TEXT} Microsoft sign-in for this development setup only works on the host PC: open ${DEV_SIGN_IN_ORIGIN} in a browser on that PC and sign in there with a Microsoft account linked to a RadioOS platform administrator.`;
    case 'dev-no-sign-in':
      return `${DEV_IDENTITY_TEXT} No Microsoft sign-in is configured for this development setup.`;
    case 'not-admin':
      return 'Reading the codeplug from the radio requires the platform administrator role. You are signed in, but your account does not have it; ask a platform administrator.';
    case 'unknown':
      return 'Reading the codeplug from the radio is limited to signed-in platform administrators.';
  }
};

/**
 * The "Read codeplug from radio" button: hidden for everyone but verified platform administrators
 * (canCaptureCodeplug; UX only -
 * the server refuses everyone else), disabled while a reading is running, waiting to be saved or
 * being saved, while the rest of the section is busy, and while the codeplug editor is open.
 */
export const captureButtonState = (o: {
  platformAdmin: boolean;
  phase: CapturePhase;
  sectionBusy: boolean;
  draftOpen: boolean;
}): 'hidden' | 'disabled' | 'enabled' => {
  if (!o.platformAdmin) return 'hidden';
  if (o.sectionBusy || o.draftOpen) return 'disabled';
  return o.phase === 'starting' || o.phase === 'pending' || o.phase === 'ready' || o.phase === 'saving' ? 'disabled' : 'enabled';
};

/** How the network is named before the server has answered (the reading itself names it). */
export const networkLabel = (networkName: string | undefined, orgId: string): string =>
  networkName?.trim() ? `"${networkName.trim()}"` : `with ID ${orgId}`;

/** The confirmation shown before asking the radio; networkText comes from networkLabel. */
export const captureConfirmText = (networkText: string): string =>
  'Read the codeplug from this radio now?\n\n' +
  "The radio reads the codeplug currently loaded in THIS app's DMR worker. It is not read from the OEM radio app, and it is not an export of everything the radio holds.\n\n" +
  'The radio must be online with DMR READY and idle; otherwise the request fails at once and nothing is queued.\n\n' +
  'Nothing is written to the radio, and no profile is loaded into any radio.\n\n' +
  `By continuing you confirm that the codeplug currently on this radio belongs to network ${networkText}. A profile saved from it is created in that network only.

` +
  'The server will require every recorded rental of this radio to be in this network, and will refuse the read if it cannot establish that; your confirmation does not override recorded history in another network. ' +
  'Even a history entirely in this network does not prove where the radio was before that history began (for example before the migration backfill). Your confirmation covers only that remaining gap.';

/** One warning line; an unknown count (null) is stated as unknown, never as "none". */
export const warningText = (w: CodeplugCaptureWarning): string =>
  `${w.message} (${w.count === null ? 'count unknown - could not be read' : `count: ${w.count}`})`;

export const NOT_LOADED_STATEMENT = 'The profile covers the supported fields only and is NOT loaded onto any radio.';

/** The reading's state in words; error_code and error are shown as the server/radio reported them. */
export const captureStatusText = (c: CodeplugCapture): string => {
  switch (c.status) {
    case 'PENDING':
      return 'Waiting for the radio to read the codeplug loaded in its DMR worker…';
    case 'READY':
      return `Read from the radio. Not saved yet; it is discarded automatically if not saved (expires ${new Date(c.expires_at).toLocaleString()}).`;
    case 'FAILED':
      return `The radio could not read its codeplug: ${c.error_code ?? 'UNKNOWN'}${c.error ? `: ${c.error}` : ''}`;
    case 'SAVED':
      return c.profile ? savedText(c.profile.name, c.network_name) : 'Saved as a profile.';
  }
};

export const savedText = (profileName: string, networkName: string): string =>
  `Saved as profile "${profileName}" in network "${networkName}". It has not been loaded into any radio.`;

/** The new profile's name: 1-64 characters after trimming (the server stays the final authority). */
export const captureNameProblem = (name: string): string | null => {
  const t = name.trim();
  if (!t) return 'A profile name is required.';
  if ([...t].length > LIMITS.profileName) return `The profile name is longer than ${LIMITS.profileName} characters.`;
  return null;
};

export const canSaveCapture = (name: string, confirmed: boolean, saving: boolean): boolean =>
  !saving && confirmed && captureNameProblem(name) === null;

export const CAPTURE_TIMEOUT_TEXT =
  'The radio did not answer in time (the server gives up after 45 seconds). Nothing was saved and nothing was written to the radio. Check that it is online with DMR READY and idle, then try again.';

const EXPLANATIONS: Record<string, string> = {
  CONFIRMATION_REQUIRED: 'The confirmation that the codeplug belongs to this network is required.',
  RADIO_NOT_FOUND: 'The radio no longer exists.',
  RADIO_OFFLINE: 'The radio is offline. Nothing was queued; try again when it is online.',
  DMR_NOT_READY: 'The DMR module of the radio is not READY. Nothing was queued.',
  CODEPLUG_WRITE_PENDING: 'A codeplug write for this radio is still pending. Wait until the radio reports it, then try again.',
  CAPTURE_IN_PROGRESS: 'Another reading of this radio is already running.',
  PROVENANCE_UNVERIFIED:
    "The radio's recorded rental history is not entirely in its current network (every recorded rental in this network, one open rental recorded by a real assignment). Reading its codeplug is refused, even with your confirmation; there is no override.",
  DMR_NOT_SUPPORTED: 'This radio does not support DMR.',
  RADIO_NOT_IN_NETWORK: 'The radio is not in a network.',
  RADIO_NOT_ENROLLED: 'The radio is not enrolled.',
  CAPTURE_NOT_FOUND: 'The reading has expired or was discarded. Read the codeplug again.',
  CAPTURE_STALE: "The radio's network or rental changed since the reading. Read the codeplug again.",
  CAPTURE_NOT_READY: 'The reading is not ready to be saved.',
  CAPTURE_SAVE_IN_PROGRESS: 'This reading is already being saved. Wait a moment and check the profile list.',
  PROFILE_NAME_EXISTS: 'A profile with this name already exists in the network. Change the name and save again.',
  INVALID_CODEPLUG: 'Refused by the codeplug or profile validation; the message names the field. Nothing is coerced or renumbered.',
  NOT_READY: 'The DMR worker was not ready.',
  BUSY: 'The DMR worker was busy.',
  WRITER_PENDING: 'A codeplug write was pending on the radio.',
  CPS_MODE: 'The radio is in programming (CPS) mode.',
  LOAD_INCOMPLETE: 'The DMR worker has not finished loading its codeplug.',
  ENCRYPTED_CHANNEL: 'The codeplug has encrypted channels, which a profile cannot hold.',
  ENCRYPTION_UNKNOWN: 'Whether channels are encrypted could not be determined.',
  UNMAPPABLE: 'The codeplug contains settings a profile cannot represent.',
  INVALID_REPORT: 'The radio sent an invalid report.',
  TIMEOUT: 'The radio timed out while reading.',
  INTERRUPTED: 'The reading was interrupted on the radio.',
  RADIO_DISCONNECTED: 'The radio disconnected during the reading.',
  IDENTITY_CHANGED: "The radio's registration (server, radio or network) changed during the reading; nothing was sent.",
  CODEPLUG_UNVERIFIED: 'The DMR module holds a codeplug this system wrote that is not verified for the current registration.',
  STAGING_FAILED: 'The radio could not hand the reading over between its app and its DMR worker.',
  NOT_SUPPORTED: "The radio's app does not support reading the codeplug.",
};

/** A short explanation of an error code (empty when there is nothing to add to the message). */
export const captureErrorExplanation = (code: string | null | undefined): string => (code ? EXPLANATIONS[code] ?? '' : '');
